import clsx from "clsx";
import { memo, useEffect, useRef } from "react";
import noImage from "@/assets/img/no-image.svg";
import { useArchiveItemPresenter } from "@/features/archive/components/useArchiveItemPresenter";
import type { ArchiveEventNotifier } from "@/features/archive/mediators/archiveEvents";
import type { ArchiveThread } from "@/features/archive/types";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";

type DesktopArchiveItemProps = {
  readonly thread: ArchiveThread;
  readonly openInNewTab?: boolean;
};

export type DesktopArchiveItemViewProps = {
  readonly archivedAtLabel: string;
  readonly imageUrl: string;
  readonly isR18Hidden: boolean;
  readonly isR18Revealed: boolean;
  readonly linkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement>;
  readonly onEvent: ArchiveEventNotifier;
  readonly textLines: number;
  readonly thread: ArchiveThread;
  readonly title: string;
};

/** PC版過去ログセルのPresenter。 */
export const DesktopArchiveItem: React.FunctionComponent<DesktopArchiveItemProps> =
  memo(function DesktopArchiveItem({
    thread,
    openInNewTab,
  }: DesktopArchiveItemProps) {
    const presentation = useArchiveItemPresenter(thread, openInNewTab);
    const { node, ...viewProps } = presentation;

    return (
      <ComponentScope node={node}>
        <DesktopArchiveItemView thread={thread} {...viewProps} />
      </ComponentScope>
    );
  });

/** propsから描画し、操作をtyped eventとして通知するPC版View。 */
export const DesktopArchiveItemView: React.FunctionComponent<
  DesktopArchiveItemViewProps
> = ({
  archivedAtLabel,
  imageUrl,
  isR18Hidden,
  isR18Revealed,
  linkAttributes,
  onEvent,
  textLines,
  thread,
  title,
}: DesktopArchiveItemViewProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isR18Revealed) linkRef.current?.focus();
  }, [isR18Revealed]);

  return (
    <article className="desktop-catalog-item" aria-label={title}>
      {isR18Hidden && (
        <button
          type="button"
          className="desktop-catalog-r18-trigger"
          aria-label={`${title}のR18画像を表示`}
          onClick={(): void => {
            onEvent({ type: "archive/item-reveal-requested" });
          }}
        />
      )}
      <CatalogThreadLink
        ref={linkRef}
        thread={{ id: thread.threadId, b: thread.b }}
        {...linkAttributes}
      >
        <figure className="desktop-catalog-thumb">
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className={clsx(isR18Hidden && "r18-spoiler-image")}
            onError={(event: React.SyntheticEvent<HTMLImageElement>): void => {
              event.currentTarget.src = noImage;
            }}
          />
        </figure>
        {textLines > 0 && (
          <p
            className="desktop-catalog-text catalog-text-clamp"
            style={
              {
                "--catalog-text-lines": textLines,
              } as React.CSSProperties
            }
          >
            {title}
          </p>
        )}
        <p className="desktop-catalog-count desktop-archive-count">
          <data value={thread.replyCount}>{thread.replyCount}レス</data>
          <CatalogBoardIcon slug={thread.b} />
          <time className="archive-saved-at" dateTime={thread.archivedAt}>
            {archivedAtLabel}
          </time>
        </p>
      </CatalogThreadLink>
    </article>
  );
};
